import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/utils/movie";

type HeroCarouselProps = {
  items: Movie[];
};

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const go = useCallback(
    (next: number) => {
      setIndex((next + items.length) % items.length);
    },
    [items.length],
  );

  useEffect(() => {
    timer.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [items.length]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (delta > 50) go(index - 1);
    else if (delta < -50) go(index + 1);
    touchStart.current = null;
  };

  return (
    <section
      className="relative h-[460px] w-full overflow-hidden bg-primary-950 md:h-[600px]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {items.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "pointer-events-none opacity-0"}`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 电影主视觉`}
            title={`${movie.title} 高清电影在线观看`}
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full items-center px-4 md:px-6">
        <div className="max-w-2xl" key={items[index]?.id}>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-line"></i>
            热门推荐
          </span>
          <h1 className="mt-4 animate-fade-up font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            {items[index]?.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-background-100">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-400"></i>
              {items[index]?.rating.toFixed(1)}
            </span>
            <span>{items[index]?.year} 年</span>
            {items[index]?.genres.map((genre) => (
              <span key={genre} className="rounded-md bg-background-50/15 px-2 py-0.5">{genre}</span>
            ))}
          </div>
          <p className="mt-4 line-clamp-3 max-w-xl animate-fade-up text-sm leading-relaxed text-background-100 md:text-base">
            {items[index]?.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${items[index]?.id}`}
              className="flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </Link>
            <Link
              to={`/detail/${items[index]?.id}`}
              className="flex items-center gap-2 rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10 whitespace-nowrap"
            >
              <i className="ri-information-line text-lg"></i>
              影片详情
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一部"
        onClick={() => go(index - 1)}
        className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-foreground-950/70 md:left-6"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一部"
        onClick={() => go(index + 1)}
        className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-foreground-950/70 md:right-6"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {items.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            aria-label={`切换到 ${movie.title}`}
            onClick={() => go(i)}
            className={`h-2 rounded-full transition-all ${i === index ? "w-7 bg-accent-500" : "w-2 bg-background-50/60"}`}
          />
        ))}
      </div>
    </section>
  );
}